import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { DatePicker } from "../components/ui/date-picker";
import { CheckCircle2, AlertTriangle, Trophy, Upload, FileText, ShieldCheck, Lock } from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "../components/ui/sonner";
import OTPSignatureDialog from "../components/signature/OTPSignatureDialog";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
// Public API — does NOT send cookies, no auth needed
const publicApi = axios.create({ baseURL: `${BACKEND}/api`, withCredentials: false });

const RELATIONS = [
  { value: "padre", label: "Padre" },
  { value: "madre", label: "Madre" },
  { value: "tutore", label: "Tutore legale" },
];

export default function PublicEnrollmentPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    player_first_name: "", player_last_name: "", player_birth_date: "",
    player_birth_place: "", player_fiscal_code: "", player_address: "",
    player_city: "", player_size: "", player_medical_notes: "",
    parent1_first_name: "", parent1_last_name: "", parent1_relation: "padre",
    parent1_fiscal_code: "", parent1_phone: "", parent1_email: "",
    parent2_first_name: "", parent2_last_name: "", parent2_phone: "", parent2_email: "",
    consent_privacy: false, consent_image_use: false, consent_medical_emergency: false,
    doc_player_id_url: "", doc_parent1_id_url: "", doc_fiscal_code_url: "", doc_contestuale_url: "",
  });

  // Digital signatures
  const [enrollSignature, setEnrollSignature] = useState(null);
  const [privacySignature, setPrivacySignature] = useState(null);
  const [showEnrollOtp, setShowEnrollOtp] = useState(false);
  const [showPrivacyOtp, setShowPrivacyOtp] = useState(false);


  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await publicApi.get(`/public/enroll/${token}`);
      setInfo(data);
      if (data.prefill) {
        setForm((f) => ({
          ...f,
          player_first_name: data.prefill.player_first_name || "",
          player_last_name: data.prefill.player_last_name || "",
          player_birth_date: data.prefill.player_birth_date || "",
          player_fiscal_code: data.prefill.player_fiscal_code || "",
          parent1_phone: data.prefill.parent1_phone || "",
          parent1_email: data.prefill.parent1_email || "",
        }));
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Invito non valido");
    }
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const uploadDoc = async (field, file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("File troppo grande (max 5MB)");
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await publicApi.post(`/public/enroll/${token}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, [field]: data.url }));
      toast.success(`${file.name} caricato`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore upload");
    }
  };

  const submit = async () => {
    // Basic validation
    const required = [
      "player_first_name", "player_last_name", "player_birth_date",
      "parent1_first_name", "parent1_last_name", "parent1_phone", "parent1_email",
    ];
    for (const f of required) {
      if (!form[f]) return toast.error(`Campo obbligatorio mancante`);
    }
    if (!form.consent_privacy) return toast.error("Devi accettare l'informativa privacy");
    if (!privacySignature) return toast.error("Firma digitalmente la liberatoria privacy");
    if (!enrollSignature) return toast.error("Firma digitalmente il modulo d'iscrizione");
    setSubmitting(true);
    try {
      await publicApi.post(`/public/enroll/${token}/submit`, {
        ...form,
        signature_id: enrollSignature.id,
        privacy_signature_id: privacySignature.id,
      });
      setSubmitted(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore invio");
    }
    setSubmitting(false);
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-slate-400">Caricamento...</div>;
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <Card className="p-8 max-w-md text-center">
          <AlertTriangle size={40} className="mx-auto text-red-500 mb-3" />
          <h2 className="font-heading text-2xl font-bold">Link non valido</h2>
          <p className="text-slate-500 mt-2">{error}</p>
          <p className="text-xs text-slate-400 mt-4">Contatta la società per ricevere un nuovo link.</p>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <Card className="p-10 max-w-md text-center">
          <CheckCircle2 size={56} className="mx-auto text-emerald-500 mb-3" />
          <h2 className="font-heading text-3xl font-bold">Iscrizione inviata!</h2>
          <p className="text-slate-500 mt-3">
            Grazie per aver compilato l'iscrizione a <strong>{info.team.name}</strong>.
            La società riceverà i dati e ti ricontatterà al più presto.
          </p>
        </Card>
      </div>
    );
  }

  const t = info.team;

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <Toaster richColors position="top-right" />
      <div className="max-w-3xl mx-auto space-y-5">
        {/* Header */}
        <Card className="p-6 border-slate-200">
          <div className="flex items-center gap-4">
            {t.logo_url ? (
              <img src={t.logo_url} alt={t.name} className="h-16 w-16 rounded-xl object-cover bg-slate-100" />
            ) : (
              <div className="h-16 w-16 rounded-xl bg-sky-500 flex items-center justify-center">
                <Trophy size={28} className="text-white" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h1 className="font-heading text-2xl font-bold truncate">{t.legal_name || t.name}</h1>
              <p className="text-sm text-slate-500">
                {t.category} · Stagione {t.season}
              </p>
            </div>
          </div>
          <div className="mt-4 p-4 bg-sky-50 border border-sky-100 rounded-xl text-sm">
            <strong>Modulo iscrizione</strong>{info.family_name ? ` per la famiglia ${info.family_name}` : ""}.
            Compila i dati del giocatore e del genitore richiedente. I documenti possono essere caricati direttamente da qui.
          </div>
        </Card>

        {/* Player data */}
        <Card className="p-6 border-slate-200">
          <h2 className="font-heading font-bold text-lg mb-4">Dati del giocatore</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Nome *</Label>
              <Input value={form.player_first_name} onChange={(e) => setForm({ ...form, player_first_name: e.target.value })} data-testid="pub-player-first-name" />
            </div>
            <div>
              <Label>Cognome *</Label>
              <Input value={form.player_last_name} onChange={(e) => setForm({ ...form, player_last_name: e.target.value })} data-testid="pub-player-last-name" />
            </div>
            <div>
              <Label>Data di nascita *</Label>
              <DatePicker value={form.player_birth_date} onChange={(v) => setForm({ ...form, player_birth_date: v })} data-testid="pub-birth-date" />
            </div>
            <div>
              <Label>Luogo di nascita</Label>
              <Input value={form.player_birth_place} onChange={(e) => setForm({ ...form, player_birth_place: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <Label>Codice Fiscale</Label>
              <Input value={form.player_fiscal_code} onChange={(e) => setForm({ ...form, player_fiscal_code: e.target.value.toUpperCase() })} placeholder="RSSMRA10A01H501Z" />
            </div>
            <div className="sm:col-span-2">
              <Label>Indirizzo residenza</Label>
              <Input value={form.player_address} onChange={(e) => setForm({ ...form, player_address: e.target.value })} />
            </div>
            <div>
              <Label>Comune</Label>
              <Input value={form.player_city} onChange={(e) => setForm({ ...form, player_city: e.target.value })} />
            </div>
            <div>
              <Label>Taglia maglia</Label>
              <Input value={form.player_size} onChange={(e) => setForm({ ...form, player_size: e.target.value })} placeholder="XS, S, M..." />
            </div>
            <div className="sm:col-span-2">
              <Label>Note mediche / Allergie</Label>
              <Textarea rows={2} value={form.player_medical_notes} onChange={(e) => setForm({ ...form, player_medical_notes: e.target.value })} />
            </div>
          </div>
        </Card>

        {/* Parents data */}
        <Card className="p-6 border-slate-200">
          <h2 className="font-heading font-bold text-lg mb-4">Genitore / Tutore richiedente</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Nome *</Label>
              <Input value={form.parent1_first_name} onChange={(e) => setForm({ ...form, parent1_first_name: e.target.value })} data-testid="pub-parent1-first" />
            </div>
            <div>
              <Label>Cognome *</Label>
              <Input value={form.parent1_last_name} onChange={(e) => setForm({ ...form, parent1_last_name: e.target.value })} data-testid="pub-parent1-last" />
            </div>
            <div>
              <Label>Relazione *</Label>
              <Select value={form.parent1_relation} onValueChange={(v) => setForm({ ...form, parent1_relation: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {RELATIONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Codice Fiscale</Label>
              <Input value={form.parent1_fiscal_code} onChange={(e) => setForm({ ...form, parent1_fiscal_code: e.target.value.toUpperCase() })} />
            </div>
            <div>
              <Label>Telefono *</Label>
              <Input value={form.parent1_phone} onChange={(e) => setForm({ ...form, parent1_phone: e.target.value })} data-testid="pub-parent1-phone" />
            </div>
            <div>
              <Label>Email *</Label>
              <Input type="email" value={form.parent1_email} onChange={(e) => setForm({ ...form, parent1_email: e.target.value })} data-testid="pub-parent1-email" />
            </div>
          </div>

          <h3 className="font-heading font-semibold mt-6 mb-3 text-sm text-slate-700">Secondo genitore (opzionale)</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Nome</Label>
              <Input value={form.parent2_first_name} onChange={(e) => setForm({ ...form, parent2_first_name: e.target.value })} />
            </div>
            <div>
              <Label>Cognome</Label>
              <Input value={form.parent2_last_name} onChange={(e) => setForm({ ...form, parent2_last_name: e.target.value })} />
            </div>
            <div>
              <Label>Telefono</Label>
              <Input value={form.parent2_phone} onChange={(e) => setForm({ ...form, parent2_phone: e.target.value })} />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={form.parent2_email} onChange={(e) => setForm({ ...form, parent2_email: e.target.value })} />
            </div>
          </div>
        </Card>

        {/* Documents */}
        <Card className="p-6 border-slate-200">
          <h2 className="font-heading font-bold text-lg mb-1">Documenti</h2>
          <p className="text-xs text-slate-500 mb-4">Carica i documenti (JPG/PNG/WEBP/PDF max 5MB ciascuno)</p>
          <div className="space-y-3">
            <DocUploadRow
              label="Carta d'Identità o Passaporto del giocatore"
              field="doc_player_id_url"
              value={form.doc_player_id_url}
              onUpload={(f) => uploadDoc("doc_player_id_url", f)}
            />
            <DocUploadRow
              label="Documento d'identità del genitore richiedente"
              field="doc_parent1_id_url"
              value={form.doc_parent1_id_url}
              onUpload={(f) => uploadDoc("doc_parent1_id_url", f)}
            />
            <DocUploadRow
              label="Codice Fiscale / Tessera sanitaria del giocatore"
              field="doc_fiscal_code_url"
              value={form.doc_fiscal_code_url}
              onUpload={(f) => uploadDoc("doc_fiscal_code_url", f)}
            />
            <DocUploadRow
              label="Modulo Contestuale firmato da entrambi i genitori (scarica dal portale società e ricarica firmato)"
              field="doc_contestuale_url"
              value={form.doc_contestuale_url}
              onUpload={(f) => uploadDoc("doc_contestuale_url", f)}
            />
          </div>
        </Card>

        {/* Consents */}
        <Card className="p-6 border-slate-200">
          <h2 className="font-heading font-bold text-lg mb-4">Consensi</h2>
          <div className="space-y-3 text-sm">
            <label className="flex gap-2 items-start cursor-pointer">
              <input type="checkbox" checked={form.consent_privacy} onChange={(e) => setForm({ ...form, consent_privacy: e.target.checked })} className="mt-1" data-testid="consent-privacy" />
              <span>
                <strong>*</strong> Ho letto e accetto l'<strong>informativa sulla privacy</strong> ai sensi del Regolamento UE 679/2016 (GDPR) e autorizzo il trattamento dei dati personali del minore per le finalità di tesseramento e organizzazione dell'attività sportiva.
              </span>
            </label>
            <label className="flex gap-2 items-start cursor-pointer">
              <input type="checkbox" checked={form.consent_image_use} onChange={(e) => setForm({ ...form, consent_image_use: e.target.checked })} className="mt-1" />
              <span>
                Autorizzo l'utilizzo dell'<strong>immagine del minore</strong> (foto/video di allenamenti e partite) per la pubblicazione su sito web e canali social della società.
              </span>
            </label>
            <label className="flex gap-2 items-start cursor-pointer">
              <input type="checkbox" checked={form.consent_medical_emergency} onChange={(e) => setForm({ ...form, consent_medical_emergency: e.target.checked })} className="mt-1" />
              <span>
                Autorizzo i Mister/Dirigenti ad attivare le procedure di <strong>emergenza sanitaria</strong> qualora si rendessero necessarie cure mediche urgenti.
              </span>
            </label>
          </div>
        </Card>

        {/* Digital Signatures */}
        <Card className="p-6 border-slate-200" data-testid="firma-digitale-card">
          <h2 className="font-heading font-bold text-lg mb-1 flex items-center gap-2">
            <ShieldCheck size={20} className="text-sky-600" /> Firma digitale
          </h2>
          <p className="text-xs text-slate-500 mb-4">
            Per validare giuridicamente l'iscrizione, firma digitalmente i due documenti tramite codice OTP via email.
          </p>
          <div className="space-y-3">
            <SignatureRow
              testid="sig-privacy-row"
              label="Liberatoria privacy & diritto d'immagine"
              signature={privacySignature}
              disabled={!form.consent_privacy}
              disabledHint="Accetta prima l'informativa privacy"
              onSign={() => {
                if (!form.parent1_email) return toast.error("Inserisci prima l'email del genitore");
                setShowPrivacyOtp(true);
              }}
            />
            <SignatureRow
              testid="sig-enrollment-row"
              label="Modulo iscrizione società"
              signature={enrollSignature}
              onSign={() => {
                if (!form.parent1_email) return toast.error("Inserisci prima l'email del genitore");
                setShowEnrollOtp(true);
              }}
            />
          </div>
        </Card>

        <Button
          size="lg"
          className="w-full bg-sky-500 hover:bg-sky-600 text-white"
          onClick={submit}
          disabled={submitting}
          data-testid="submit-enrollment"
        >
          {submitting ? "Invio in corso..." : "Invia iscrizione"}
        </Button>

        <p className="text-center text-xs text-slate-400 py-4">
          {t.legal_name || t.name}
          {t.fiscal_code ? ` · C.F. ${t.fiscal_code}` : ""}
          {t.address ? ` · ${t.address}` : ""}
        </p>
      </div>

      <OTPSignatureDialog
        open={showPrivacyOtp}
        onOpenChange={setShowPrivacyOtp}
        documentType="privacy_release"
        documentRef={token}
        teamId={info?.team_id}
        defaultEmail={form.parent1_email}
        defaultName={`${form.parent1_first_name} ${form.parent1_last_name}`.trim()}
        signerRoleLabel={form.parent1_relation || "Genitore"}
        onSigned={setPrivacySignature}
        title="Firma Liberatoria privacy & immagine"
        description="Firma digitalmente la liberatoria privacy e diritto d'immagine per il minore. Riceverai un codice via email."
      />
      <OTPSignatureDialog
        open={showEnrollOtp}
        onOpenChange={setShowEnrollOtp}
        documentType="enrollment"
        documentRef={token}
        teamId={info?.team_id}
        defaultEmail={form.parent1_email}
        defaultName={`${form.parent1_first_name} ${form.parent1_last_name}`.trim()}
        signerRoleLabel={form.parent1_relation || "Genitore"}
        onSigned={setEnrollSignature}
        title="Firma modulo iscrizione"
        description="Conferma e firma digitalmente i dati inseriti. Riceverai un codice via email."
      />
    </div>
  );
}

function DocUploadRow({ label, value, onUpload }) {
  return (
    <div className="flex items-center justify-between gap-3 p-3 rounded-lg border border-slate-200 bg-slate-50/40">
      <div className="flex items-center gap-2 flex-1 min-w-0">
        {value ? (
          <CheckCircle2 size={18} className="text-emerald-500 shrink-0" />
        ) : (
          <FileText size={18} className="text-slate-400 shrink-0" />
        )}
        <span className="text-sm truncate">{label}</span>
      </div>
      <label className="cursor-pointer">
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp,application/pdf"
          className="hidden"
          onChange={(e) => onUpload(e.target.files?.[0])}
        />
        <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-sky-100 hover:bg-sky-200 text-sky-700 text-xs font-medium">
          <Upload size={12} /> {value ? "Cambia" : "Carica"}
        </span>
      </label>
    </div>
  );
}

function SignatureRow({ label, signature, onSign, disabled, disabledHint, testid }) {
  const signed = !!signature;
  return (
    <div
      className={`flex items-center justify-between gap-3 p-3 rounded-lg border bg-slate-50/40 ${
        disabled ? "border-slate-200 opacity-60" : "border-slate-200"
      }`}
      data-testid={testid}
    >
      <div className="flex items-center gap-2 flex-1 min-w-0">
        {signed ? (
          <CheckCircle2 size={18} className="text-emerald-500 shrink-0" />
        ) : (
          <Lock size={18} className="text-slate-400 shrink-0" />
        )}
        <div className="min-w-0">
          <div className="text-sm truncate">{label}</div>
          {signed ? (
            <div className="text-xs text-emerald-600 truncate">
              Firmato da {signature.signer_name || signature.signer_email}
            </div>
          ) : disabled ? (
            <div className="text-xs text-amber-600">{disabledHint}</div>
          ) : null}
        </div>
      </div>
      <button
        type="button"
        onClick={onSign}
        disabled={signed || disabled}
        className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium ${
          signed
            ? "bg-emerald-100 text-emerald-700 cursor-default"
            : disabled
            ? "bg-slate-100 text-slate-400 cursor-not-allowed"
            : "bg-sky-100 hover:bg-sky-200 text-sky-700"
        }`}
      >
        <ShieldCheck size={12} /> {signed ? "Firmato" : "Firma"}
      </button>
    </div>
  );
}
